import { useState, useEffect, useCallback } from 'react';
import { doc, updateDoc, collection, getDocs } from 'firebase/firestore';
import { db, getAllPatients, deletePatientCase } from '../firebase';
import { User, Patient } from '../types';
import { canViewPatient } from '../utils/roleUtils';

export function usePatients(user: User | null) {
  const [patients, setPatients] = useState<Patient[]>([]);
  const [filteredPatients, setFilteredPatients] = useState<Patient[]>([]);
  const [isPatientLoading, setIsPatientLoading] = useState(false);
  const [patientManagementSearchTerm, setPatientManagementSearchTerm] = useState('');
  const [patientStatusFilter, setPatientStatusFilter] = useState('all');
  const [filterByUserId, setFilterByUserId] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [userPrograms, setUserPrograms] = useState<string[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [userGroups, setUserGroups] = useState<string[]>([]);

  // Helper function to sort patients: new first, archived (inactive) last
  const sortPatientsByStatus = useCallback((patients: Patient[]): Patient[] => {
    const statusOrder: { [key: string]: number } = {
      'new': 1,
      'active': 2,
      'completed': 3,
      'inactive': 4
    };
    
    return [...patients].sort((a, b) => {
      const statusA = (a.status?.toLowerCase() || 'active');
      const statusB = (b.status?.toLowerCase() || 'active');
      const orderA = statusOrder[statusA] || 5;
      const orderB = statusOrder[statusB] || 5;
      return orderA - orderB;
    });
  }, []);

  // Helper function to load user's programs and groups
  const loadUserAssignments = useCallback(async () => {
    if (!user) return { programs: [], groups: [] };
    
    try {
      // Load programs where user is a manager
      const programsSnapshot = await getDocs(collection(db, 'programs'));
      const programs = programsSnapshot.docs
        .filter(doc => {
          const data = doc.data();
          return Array.isArray(data.managers) && data.managers.includes(user.id);
        })
        .map(doc => doc.id);
      
      // Load groups where user is a manager
      const groupsSnapshot = await getDocs(collection(db, 'groups'));
      const groups = groupsSnapshot.docs
        .filter(doc => {
          const data = doc.data();
          return Array.isArray(data.managers) && data.managers.includes(user.id);
        })
        .map(doc => doc.id);
      
      return { programs, groups };
    } catch (error) {
      console.error('Error loading user assignments:', error);
      return { programs: [], groups: [] };
    }
  }, [user]);

  const loadPatients = useCallback(async () => {
    if (!user) return;
    
    setIsPatientLoading(true);
    try {
      // Load user assignments FIRST to ensure they're available for filtering
      const { programs, groups } = await loadUserAssignments();
      setUserPrograms(programs);
      setUserGroups(groups);
      
      const patientsData = await getAllPatients();
      setPatients(patientsData);
      
      // Apply current filters with the freshly loaded programs/groups
      let filtered = patientsData;
      
      // Apply role-based visibility using the new permission system
      filtered = filtered.filter(p => canViewPatient(user, p, programs, groups));
      
      if (patientStatusFilter !== 'all') {
        filtered = filtered.filter(p => p.status?.toLowerCase() === patientStatusFilter.toLowerCase());
      }
      
      // Filter by assigned user if specified
      if (filterByUserId) {
        filtered = filtered.filter(p => 
          Array.isArray(p.assignedAdmins) && p.assignedAdmins.includes(filterByUserId)
        );
      }
      
      // Sort: new first, archived (inactive) last
      filtered = sortPatientsByStatus(filtered);
      
      setFilteredPatients(filtered);
    } catch (error) {
      console.error('Error loading patients:', error);
    } finally {
      setIsPatientLoading(false);
    }
  }, [user, patientStatusFilter, filterByUserId, loadUserAssignments, sortPatientsByStatus]);

  useEffect(() => {
    loadPatients();
  }, [loadPatients]);

  const handlePatientStatusChange = useCallback(async (caseId: string, newStatus: string) => {
    try {
      const patientDocRef = doc(db, 'patients', caseId);
      await updateDoc(patientDocRef, {
        status: newStatus,
        updatedAt: new Date()
      });
      
      setPatients(prevPatients => 
        prevPatients.map(patient => 
          patient.caseId === caseId 
            ? { ...patient, status: newStatus as 'new' | 'active' | 'inactive' | 'completed' }
            : patient
        )
      );
      
      setFilteredPatients(prevFiltered => {
        const updated = prevFiltered.map(patient => 
          patient.caseId === caseId 
            ? { ...patient, status: newStatus as 'new' | 'active' | 'inactive' | 'completed' }
            : patient
      );
        // Re-sort after status change: new first, archived (inactive) last
        return sortPatientsByStatus(updated);
      });
    } catch (error) {
      console.error('Error updating patient status:', error);
    }
  }, []);

  const handleDeletePatient = useCallback(async (caseId: string) => {
    if (!user) return { success: false };
    
    try {
      const result = await deletePatientCase(caseId, user.id);
      if (result.success) {
        await loadPatients();
      }
      return result;
    } catch (error) {
      console.error('Error deleting patient:', error);
      return { success: false };
    }
  }, [user, loadPatients]);

  const handlePatientManagementSearch = useCallback((searchTerm: string) => {
    setPatientManagementSearchTerm(searchTerm);
    
    let filtered = patients;
    
    // Apply role-based visibility using cached state
    filtered = filtered.filter(p => canViewPatient(user, p, userPrograms, userGroups));
    
    if (patientStatusFilter !== 'all') {
      filtered = filtered.filter(p => p.status?.toLowerCase() === patientStatusFilter.toLowerCase());
    }
    
    if (filterByUserId) {
      filtered = filtered.filter(p => 
        Array.isArray(p.assignedAdmins) && p.assignedAdmins.includes(filterByUserId)
      );
    }
    
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(p => 
        p.caseId?.toLowerCase().includes(searchLower)
      );
    }
    
    // Sort: new first, archived (inactive) last
    filtered = sortPatientsByStatus(filtered);
    
    setFilteredPatients(filtered);
  }, [patients, patientStatusFilter, filterByUserId, user, userPrograms, userGroups, sortPatientsByStatus]);

  const handlePatientStatusFilterChange = useCallback((filter: string) => {
    setPatientStatusFilter(filter);
    
    let filtered = patients;
    
    // Apply role-based visibility using cached state
    filtered = filtered.filter(p => canViewPatient(user, p, userPrograms, userGroups));
    
    if (filter !== 'all') {
      filtered = filtered.filter(p => p.status?.toLowerCase() === filter.toLowerCase());
    }
    
    if (filterByUserId) {
      filtered = filtered.filter(p => 
        Array.isArray(p.assignedAdmins) && p.assignedAdmins.includes(filterByUserId)
      );
    }
    
    if (patientManagementSearchTerm) {
      const searchLower = patientManagementSearchTerm.toLowerCase();
      filtered = filtered.filter(p => 
        p.caseId?.toLowerCase().includes(searchLower)
      );
    }
    
    // Sort: new first, archived (inactive) last
    filtered = sortPatientsByStatus(filtered);
    
    setFilteredPatients(filtered);
  }, [patients, patientManagementSearchTerm, filterByUserId, user, userPrograms, userGroups, sortPatientsByStatus]);

  const handleFilterByUser = useCallback((userId: string | null) => {
    setFilterByUserId(userId);
  }, []);

  return {
    patients,
    filteredPatients,
    isPatientLoading,
    patientManagementSearchTerm,
    patientStatusFilter,
    filterByUserId,
    handlePatientStatusChange,
    handleDeletePatient,
    handlePatientManagementSearch,
    handlePatientStatusFilterChange,
    handleFilterByUser,
    refreshPatients: loadPatients
  };
}


